import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";
import { Users as UsersIcon, Send } from "lucide-react";
import { CandidateDrawer, DEFAULT_STAGES } from "@/components/CandidateDrawer.jsx";
import { AttachmentPicker } from "@/components/AttachmentPicker.jsx";
import { fmtIST } from "@/lib/time";
const SOURCES = ["google_form", "referral", "outreach", "wati_inbound", "direct", "manual"];
const SORTS = [
  { k: "newest", label: "Newest first" },
  { k: "oldest", label: "Oldest first" },
  { k: "score_desc", label: "AI score · high → low" },
  { k: "score_asc", label: "AI score · low → high" },
  { k: "name", label: "Name A → Z" },
];
const VIEWS = [
  { k: "all", label: "All" },
  { k: "pipeline", label: "Active pipeline" },
  { k: "talent", label: "Talent pool" },
  { k: "referred", label: "Referred" },
  { k: "knocked", label: "Knocked out" },
  { k: "partials", label: "Partials" },
];

export default function Candidates() {
  const [subs, setSubs] = useState([]);
  const [forms, setForms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [view, setView] = useState("pipeline");
  const [mode, setMode] = useState("table");
  const [formFilter, setFormFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [textQ, setTextQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [drawer, setDrawer] = useState(null);
  const [compose, setCompose] = useState(null);
  const [newGroup, setNewGroup] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");

  const load = async () => {
    const [s, f, g, t] = await Promise.all([api.get("/submissions"), api.get("/forms"), api.get("/groups"), api.get("/comms/templates")]);
    setSubs(s.data); setForms(f.data); setGroups(g.data); setTemplates(t.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = subs.filter((s) => formFilter === "all" || s.form_id === formFilter);
    if (sourceFilter !== "all") rows = rows.filter((s) => (s.source || s.utm?.source || "direct") === sourceFilter);
    if (textQ) {
      const q = textQ.toLowerCase();
      rows = rows.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q) || (s.phone || "").toLowerCase().includes(q));
    }
    if (view === "pipeline") rows = rows.filter((s) => !s.partial && !s.knockout && !["Rejected","Talent pool"].includes(s.stage));
    if (view === "talent") rows = rows.filter((s) => s.stage === "Talent pool");
    if (view === "referred") rows = rows.filter((s) => (s.source || s.utm?.source || "") === "referral" || (s.tags || []).includes("Referred"));
    if (view === "knocked") rows = rows.filter((s) => s.knockout);
    if (view === "partials") rows = rows.filter((s) => s.partial);
    if (groupFilter) {
      const g = groups.find((x) => x.group_id === groupFilter);
      if (g?.kind === "manual") rows = rows.filter((s) => (g.submission_ids || []).includes(s.submission_id));
      if (g?.kind === "smart") {
        const r = g.rules || {};
        rows = rows.filter((s) =>
          (!r.stage || s.stage === r.stage) &&
          (!r.tag || (s.tags || []).includes(r.tag)) &&
          (!r.city || s.city === r.city) &&
          (!r.source || (s.source || s.utm?.source || "") === r.source) &&
          (r.min_score == null || (s.ai_score || 0) >= r.min_score)
        );
      }
    }
    const toTs = (s) => new Date(s.submitted_at || s.started_at || 0).getTime();
    if (sort === "newest") rows.sort((a, b) => toTs(b) - toTs(a));
    else if (sort === "oldest") rows.sort((a, b) => toTs(a) - toTs(b));
    else if (sort === "score_desc") rows.sort((a, b) => (b.ai_score || -1) - (a.ai_score || -1));
    else if (sort === "score_asc") rows.sort((a, b) => (a.ai_score || 999) - (b.ai_score || 999));
    else if (sort === "name") rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return rows;
  }, [subs, view, formFilter, sourceFilter, textQ, sort, groupFilter, groups]);

  const stats = useMemo(() => {
    const inPipe = subs.filter((s) => !s.partial && !s.knockout && !["Rejected","Talent pool"].includes(s.stage)).length;
    const bookedThisWeek = subs.filter((s) => s.booking && new Date(s.booking.slot_iso) > new Date(Date.now() - 7*86400000)).length;
    const scores = subs.filter((s) => typeof s.ai_score === "number").map((s) => s.ai_score);
    const avg = scores.length ? (scores.reduce((a,b)=>a+b,0)/scores.length).toFixed(0) : "—";
    return [
      { label: "In pipeline", value: inPipe },
      { label: "Booked (7d)", value: bookedThisWeek, accent: "pass" },
      { label: "Avg AI score", value: avg },
      { label: "Partials", value: subs.filter((s) => s.partial).length, accent: subs.filter((s) => s.partial).length > 0 ? "warn" : "neutral" },
    ];
  }, [subs]);

  const formsById = useMemo(() => Object.fromEntries(forms.map((f) => [f.form_id, f])), [forms]);
  const onChanged = (s) => { setSubs((p) => p.map((x) => (x.submission_id === s.submission_id ? s : x))); setDrawer(s); };
  const openCompose = () => setCompose({ channel: "both", body: "Hi {{first_name}}, …", wa_body: "", subject: "A quick update on your application", schedule_at: "" });
  const messageOne = (s) => { setSelected(new Set([s.submission_id])); setDrawer(null); openCompose(); };

  const togglePick = (id) => setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allShown = filtered.length && filtered.every((f) => selected.has(f.submission_id));
  const toggleAll = () => setSelected(allShown ? new Set() : new Set(filtered.map((f) => f.submission_id)));

  const bulk = async (patch) => {
    if (selected.size === 0) return toast.warning("Pick candidates first");
    await api.post("/candidates/bulk", { submission_ids: [...selected], ...patch });
    toast.success(`Applied to ${selected.size}`); setSelected(new Set()); load();
  };

  const createGroup = async () => {
    if (!newGroup.name) return toast.warning("Name required");
    const body = { ...newGroup, submission_ids: newGroup.kind === "manual" ? [...selected] : [] };
    await api.post("/groups", body);
    toast.success("Group created"); setNewGroup(null); load();
  };

  const sendComms = async () => {
    if (!compose.body) return toast.warning("Body required");
    const payload = {
      submission_ids: compose.use_group ? [] : [...selected],
      group_id: compose.use_group ? compose.group_id : null,
      channel: compose.channel,
      body: compose.body,
      wa_body: compose.wa_body,
      subject: compose.subject,
      wati_template_name: compose.wati_template_name || null,
      schedule_at: compose.schedule_at || null,
      attachment_ids: (compose.attachments || []).map((a) => a.file_id),
    };
    const r = await api.post("/comms/send", payload).catch((e) => { toast.error(e?.response?.data?.detail || "Send failed"); return null; });
    if (!r) return;
    const d = r.data;
    if (payload.schedule_at) toast.success(`Scheduled ${d.scheduled} message(s)`);
    else {
      if (d.delivered) toast.success(`Delivered ${d.delivered} message(s)`);
      if (d.queued) toast.warning(`${d.queued} email(s) queued, not sent: connect Google in Settings`);
      if (d.failed) toast.error(`${d.failed} message(s) failed. Open the candidate's Messages for details (WhatsApp needs an approved template; files outside a 24h chat use the document template)`);
    }
    setCompose(null); setSelected(new Set());
  };

  return (
    <div>
      <PageHeader
        eyebrow="HIRING · CANDIDATES"
        title="Candidates"
        subtitle={`${filtered.length} shown · ${subs.length} total`}
        stats={stats}
        action={
          <div className="flex gap-2">
            {selected.size > 0 && (
              <>
                <button onClick={() => bulk({ stage: "Screened" })} className="tt-btn-secondary text-sm">Move to Screened</button>
                <button onClick={() => bulk({ add_tag: "Promising" })} className="tt-btn-secondary text-sm">+ tag Promising</button>
                <button onClick={openCompose} className="tt-btn-primary text-sm flex items-center gap-1" data-testid="open-send-comms"><Send size={13} /> Send comms</button>
                <button onClick={() => setNewGroup({ name: "", kind: "manual", rules: {} })} className="tt-btn-secondary text-sm flex items-center gap-1"><UsersIcon size={13} /> New group</button>
              </>
            )}
          </div>
        }
      />

      {/* Views + filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {VIEWS.map((v) => (
          <button key={v.k} onClick={() => setView(v.k)} data-testid={`candidates-view-${v.k}`}
            className={`px-3 py-1 rounded-md text-xs transition-colors ${view === v.k ? "bg-[#1A1A1A] text-[#F4F4F5] border border-[#262626]" : "text-[#A1A1AA] hover:text-[#F4F4F5] border border-transparent"}`}>
            {v.label}
          </button>
        ))}
        <div className="flex-1" />
        <input className="tt-input !w-56 text-xs" placeholder="Search name / email / phone" value={textQ} onChange={(e) => setTextQ(e.target.value)} data-testid="candidates-search" />
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="tt-input !w-auto text-xs" data-testid="candidates-source-filter">
          <option value="all">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="tt-input !w-auto text-xs" data-testid="candidates-sort">
          {SORTS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
        </select>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className="tt-input !w-auto text-xs">
          <option value="">All groups</option>
          {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name} ({g.count})</option>)}
        </select>
        <select value={formFilter} onChange={(e) => setFormFilter(e.target.value)} className="tt-input !w-auto text-xs">
          <option value="all">All forms</option>
          {forms.map((f) => <option key={f.form_id} value={f.form_id}>{f.title}</option>)}
        </select>
        <div className="inline-flex rounded-md border border-[#262626] overflow-hidden">
          <button onClick={() => setMode("table")} className={`px-3 py-1 text-xs ${mode === "table" ? "bg-[#1A1A1A] text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>Table</button>
          <button onClick={() => setMode("kanban")} className={`px-3 py-1 text-xs ${mode === "kanban" ? "bg-[#1A1A1A] text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>Kanban</button>
        </div>
      </div>

      {mode === "table" && (
        <div className="tt-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F]">
              <tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase">
                <th className="px-4 py-2 w-8"><input type="checkbox" checked={!!allShown} onChange={toggleAll} className="accent-[#5B9BFF]" /></th>
                <th className="text-left px-4 py-2">Candidate</th>
                <th className="text-left px-4 py-2">City</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Booked</th>
                <th className="text-left px-4 py-2">Stage</th>
                <th className="text-right px-4 py-2">AI</th>
                <th className="text-left px-4 py-2">Tags</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.submission_id} className="border-t border-[#1E1E1E] hover:bg-[#121212]" data-testid={`candidate-row-${s.submission_id}`}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(s.submission_id)} onChange={() => togglePick(s.submission_id)} className="accent-[#5B9BFF]" onClick={(e) => e.stopPropagation()} /></td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => setDrawer(s)}>
                    <div className="font-medium">{s.name || "Anonymous"}</div>
                    <div className="text-xs font-mono text-[#71717A]">{s.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3">{s.city || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[#A1A1AA]">{s.source || s.utm?.source || "direct"}</td>
                  <td className="px-4 py-3 text-xs">{s.booking?.slot_iso ? <span className="tt-badge-pass">{fmtIST(s.booking.slot_iso)}</span> : <span className="text-[#71717A]">—</span>}</td>
                  <td className="px-4 py-3">
                    {s.partial ? <span className="tt-badge-warn">Partial</span>
                      : s.knockout ? <span className="tt-badge-danger">Knockout</span>
                      : <span className="tt-badge-info">{s.stage}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{typeof s.ai_score === "number" ? Math.round(s.ai_score) : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">{(s.tags || []).map((t) => <span key={t} className="tt-badge-neutral">{t}</span>)}</div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#71717A]">
                  No candidates in this view. <span className="text-[#A1A1AA]">Try "All" or a different filter.</span>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {mode === "kanban" && (
        <div className="overflow-x-auto">
          <div className="flex gap-3 min-w-max pb-4">
            {DEFAULT_STAGES.map((st) => {
              const col = filtered.filter((s) => s.stage === st);
              return (
                <div key={st} className="w-[260px] shrink-0">
                  <div className="flex items-center justify-between mb-2 px-2">
                    <span className="font-mono text-[10px] tracking-[0.18em] text-[#71717A] uppercase">{st}</span>
                    <span className="font-mono text-xs text-[#71717A]">{col.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.map((s) => (
                      <button key={s.submission_id} onClick={() => setDrawer(s)} className="w-full text-left tt-card p-3 hover:border-[#5B9BFF]">
                        <div className="font-medium text-sm truncate">{s.name || "Anonymous"}</div>
                        <div className="text-xs font-mono text-[#71717A] truncate">{s.email}</div>
                        {typeof s.ai_score === "number" && (
                          <div className="mt-1 font-mono text-xs" style={{ color: s.ai_score >= 70 ? "#22C55E" : s.ai_score >= 50 ? "#F5A524" : "#EF4444" }}>AI {Math.round(s.ai_score)}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {drawer && <CandidateDrawer sub={drawer} form={formsById[drawer.form_id]} onClose={() => setDrawer(null)} onChanged={onChanged} onMessage={messageOne} />}

      {/* Compose */}
      {compose && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setCompose(null)}>
          <div className="w-full max-w-xl tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Send comms</h2>
            <div className="flex gap-2 mb-3">
              <select value={compose.channel} onChange={(e) => setCompose({ ...compose, channel: e.target.value })} className="tt-input !w-auto" data-testid="compose-channel">
                <option value="both">Both · Email + WhatsApp</option>
                <option value="whatsapp">WhatsApp (Wati)</option>
                <option value="email">Email (Gmail)</option>
              </select>
              <select value={compose.template_id || ""} onChange={(e) => { const t = templates.find((x) => x.template_id === e.target.value); if (t) { const wa = t.channel === "whatsapp" ? t : templates.find((x) => x.channel === "whatsapp" && x.name === `${t.name} (WhatsApp)`); setCompose({ ...compose, template_id: t.template_id, body: t.body, subject: t.subject, wati_template_name: wa?.wati_template_name || t.wati_template_name, wa_body: wa && wa !== t ? wa.body : compose.wa_body, attachments: [...(compose.attachments || []), ...(t.attachments?.length ? t.attachments : (wa?.attachments || []))].filter((a, i, arr) => arr.findIndex((x) => x.file_id === a.file_id) === i) }); } }} className="tt-input flex-1" data-testid="compose-template-select">
                <option value="">— template —</option>
                {templates.filter((t) => compose.channel === "both" || t.channel === compose.channel).map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
              </select>
            </div>
            {(compose.channel === "email" || compose.channel === "both") && <input className="tt-input mb-3" placeholder="Email subject" value={compose.subject || ""} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="compose-subject" />}
            <div className="tt-label mb-1">{compose.channel === "both" ? "Email body" : "Message body"}</div>
            <textarea rows={5} className="tt-input mb-3 text-sm" value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="compose-body" />
            {compose.channel === "both" && (
              <>
                <div className="tt-label mb-1">WhatsApp body <span className="text-[#71717A] normal-case">· leave blank to reuse the email body</span></div>
                <textarea rows={3} className="tt-input mb-3 text-sm" value={compose.wa_body || ""} onChange={(e) => setCompose({ ...compose, wa_body: e.target.value })} data-testid="compose-wa-body" />
              </>
            )}
            {(compose.channel === "whatsapp" || compose.channel === "both") && (
              <input className="tt-input mb-3 font-mono text-xs" placeholder="Wati template name (optional · leave empty for session message)" value={compose.wati_template_name || ""} onChange={(e) => setCompose({ ...compose, wati_template_name: e.target.value })} data-testid="compose-wati-template" />
            )}
            <div className="text-xs text-[#71717A] mb-3">Variables: <code className="font-mono">{"{{first_name}} {{role}} {{form_link}} {{booking_link}}"}</code></div>
            <AttachmentPicker value={compose.attachments || []} onChange={(attachments) => setCompose({ ...compose, attachments })} testId="compose-attachments" />
            <div className="mb-3">
              <div className="tt-label mb-1">Schedule for later <span className="text-[#71717A] normal-case">· leave empty to send now</span></div>
              <input type="datetime-local" className="tt-input text-sm font-mono" value={compose.schedule_at || ""} onChange={(e) => setCompose({ ...compose, schedule_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="compose-schedule" />
            </div>
            <div className="text-sm text-[#A1A1AA] mb-4">To: <span className="text-[#F4F4F5]">{selected.size} selected candidate{selected.size === 1 ? "" : "s"}</span></div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCompose(null)} className="tt-btn-secondary text-sm">Cancel</button>
              <button onClick={sendComms} className="tt-btn-primary text-sm" data-testid="compose-send">{compose.schedule_at ? "Schedule" : "Send now"}</button>
            </div>
          </div>
        </div>
      )}

      {/* New group */}
      {newGroup && (
        <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={() => setNewGroup(null)}>
          <div className="w-full max-w-md tt-card p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">New candidate group</h2>
            <input className="tt-input mb-3" placeholder="Group name" value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} />
            <select value={newGroup.kind} onChange={(e) => setNewGroup({ ...newGroup, kind: e.target.value, rules: {} })} className="tt-input mb-3">
              <option value="manual">Manual · from selected ({selected.size})</option>
              <option value="smart">Smart · rule-based</option>
            </select>
            {newGroup.kind === "smart" && (
              <div className="space-y-2 mb-3">
                <input className="tt-input" placeholder="Stage (exact)" value={newGroup.rules.stage || ""} onChange={(e) => setNewGroup({ ...newGroup, rules: { ...newGroup.rules, stage: e.target.value || undefined } })} />
                <input className="tt-input" placeholder="Tag" value={newGroup.rules.tag || ""} onChange={(e) => setNewGroup({ ...newGroup, rules: { ...newGroup.rules, tag: e.target.value || undefined } })} />
                <input className="tt-input" placeholder="City" value={newGroup.rules.city || ""} onChange={(e) => setNewGroup({ ...newGroup, rules: { ...newGroup.rules, city: e.target.value || undefined } })} />
                <input className="tt-input" placeholder="Source" value={newGroup.rules.source || ""} onChange={(e) => setNewGroup({ ...newGroup, rules: { ...newGroup.rules, source: e.target.value || undefined } })} />
                <input type="number" className="tt-input font-mono" placeholder="Min AI score" value={newGroup.rules.min_score || ""} onChange={(e) => setNewGroup({ ...newGroup, rules: { ...newGroup.rules, min_score: e.target.value ? parseFloat(e.target.value) : undefined } })} />
              </div>
            )}
            <div className="flex justify-end gap-2"><button onClick={() => setNewGroup(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={createGroup} className="tt-btn-primary text-sm">Create</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
